import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Skeleton,
} from '@mui/material';
import { CalendarMonth, List as ListIcon } from '@mui/icons-material';
import { useRenewals } from '../../hooks/useRenewals';
import { formatRenewalDate, maskPolicyNumber, daysUntil } from '../../utils/renewalHelpers';
import RenewalCalendarGrid from '../../components/renewals/RenewalCalendarGrid';
import { FEATURES } from '../../config/features';
import t from '../../utils/renewalsTranslations';

const locale = (typeof window !== 'undefined' && localStorage.getItem('locale')) || 'en';

export default function RenewalCalendar() {
  const navigate = useNavigate();
  const { upcoming, loading, error } = useRenewals();
  const [viewMode, setViewMode] = useState('calendar');

  const renewalsForGrid = useMemo(
    () =>
      (upcoming || []).map((p) => ({
        ...p,
        policyId: p.id || p.policyId,
        policyName: p.policy_name || p.policyName,
        expiryDate: p.end_date || p.expiryDate,
        daysUntilExpiry: p.daysUntilExpiry ?? daysUntil(p.end_date || p.expiryDate),
        insurerName: p.insurer_name || p.insurerName,
        betterDealsFound: p.better_deals_found ?? p.betterDealsFound ?? 0,
      })),
    [upcoming]
  );

  if (loading)
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Skeleton variant="rectangular" height={600} />
      </Container>
    );
  if (error)
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Typography color="error">{error}</Typography>
      </Container>
    );

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5">{t('renewals.calendar.title')}</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant={viewMode === 'list' ? 'contained' : 'outlined'}
            size="small"
            startIcon={<ListIcon />}
            onClick={() => setViewMode('list')}
          >
            {t('renewals.calendar.listView')}
          </Button>
          <Button
            variant={viewMode === 'calendar' ? 'contained' : 'outlined'}
            size="small"
            startIcon={<CalendarMonth />}
            onClick={() => setViewMode('calendar')}
          >
            Calendar
          </Button>
          <Button variant="outlined" size="small" onClick={() => navigate('/renewals')}>
            Dashboard
          </Button>
        </Box>
      </Box>

      {renewalsForGrid.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography color="text.secondary">{t('renewals.calendar.noRenewals')}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{t('renewals.calendar.noRenewalsDesc')}</Typography>
        </Box>
      ) : viewMode === 'calendar' && FEATURES.renewalCalendar ? (
        <Box sx={{ height: 600, bgcolor: 'background.paper', p: 2, borderRadius: 1 }}>
          <RenewalCalendarGrid
            renewals={renewalsForGrid}
            locale={locale}
            onSelectEvent={(r) => navigate(`/renewals/${r.policyId || r.id}`)}
          />
        </Box>
      ) : (
        <List>
          {renewalsForGrid.map((p) => (
            <ListItem key={p.policyId || p.id} disablePadding>
              <ListItemButton onClick={() => navigate(`/renewals/${p.policyId || p.id}`)}>
                <ListItemText
                  primary={p.policyName || maskPolicyNumber(p.policy_number)}
                  secondary={`${formatRenewalDate(p.expiryDate || p.end_date)} · ${p.insurerName || ''}`}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      )}
    </Container>
  );
}
